import { FC, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useImageUpload } from '@/hooks/useImageUpload';
import { PulseButton } from '@/components/ui/PulseButton';
import { COPY } from '@/lib/copyDictionary';
import { useGamenics, GamenicsEvent } from '@/hooks/useGamenics';
import { api } from '@/services/api';

interface ThemeCreationModalProps {
    onClose: () => void;
    onSuccess: () => void;
    mode?: 'admin' | 'user';
}

export const ThemeCreationModal: FC<ThemeCreationModalProps> = ({ onClose, onSuccess, mode = 'admin' }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [saving, setSaving] = useState(false);
    const { playSound } = useGamenics();

    // Use the upload hook
    const { uploadImage, uploading, error: uploadError } = useImageUpload();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);

        const data = { title, description, image_url: imageUrl };
        const res = mode === 'admin'
            ? await api.createTheme(data)
            : await api.suggestTheme(data);

        setSaving(false);
        if (res.success) {
            onSuccess();
            onClose();
        } else {
            alert(res.error || 'Failed to submit theme');
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const result = await uploadImage(e.target.files[0]);
            if (result.success && result.data) {
                setImageUrl(result.data.url);
            }
        }
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-ink/70 backdrop-blur-sm"
                    onClick={onClose}
                />

                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="relative bg-surface border-2 border-ink rounded-2xl p-6 w-full max-w-md shadow-2xl"
                >
                    <h2 className="text-xl font-bold font-display text-ink mb-6">
                        {mode === 'admin' ? 'テーマを新規作成' : 'テーマを提案する'}
                    </h2>

                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div>
                            <label className="text-xs text-ink-3 uppercase tracking-widest block mb-1">テーマタイトル</label>
                            <input
                                type="text"
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                className="w-full bg-cream-2 border-2 border-ink/15 rounded-lg p-3 text-ink focus:border-primary outline-none transition-colors"
                                required
                                maxLength={50}
                                placeholder="例: 声優オーディション"
                            />
                        </div>

                        <div>
                            <label className="text-xs text-ink-3 uppercase tracking-widest block mb-1">説明文</label>
                            <textarea
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                className="w-full bg-cream-2 border-2 border-ink/15 rounded-lg p-3 text-ink focus:border-primary outline-none transition-colors h-24"
                                required
                                placeholder="テーマの詳細な説明を記入してください..."
                            />
                        </div>

                        <div>
                            <label className="text-xs text-ink-3 uppercase tracking-widest block mb-1">ヘッダー画像 (任意)</label>

                            <div className="flex items-center gap-4 mb-3">
                                <div className="w-16 h-16 rounded-md border-2 border-ink overflow-hidden bg-cream-2">
                                    {imageUrl ? <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-cream-2 flex items-center justify-center text-xs text-ink-3">No Img</div>}
                                </div>
                                <div className="flex-1">
                                    <label className="cursor-pointer bg-cream-2 hover:bg-cream text-ink text-sm py-2 px-4 rounded-lg transition-colors inline-block text-center w-full">
                                        {uploading ? COPY.uploading : COPY.uploadImage}
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            disabled={uploading}
                                        />
                                    </label>
                                </div>
                            </div>

                            {uploadError && <p className="text-red-400 text-xs">{uploadError}</p>}
                        </div>

                        <div className="flex gap-2 mt-6">
                            <PulseButton
                                type="button"
                                variant="ghost"
                                onClick={() => { playSound(GamenicsEvent.TAP_CANCEL); onClose(); }}
                                disabled={saving || uploading}
                                className="flex-1"
                            >
                                {COPY.cancel}
                            </PulseButton>
                            <PulseButton
                                type="submit"
                                isPrimary={true}
                                disabled={saving || uploading}
                                className="flex-1"
                                onClick={() => playSound(GamenicsEvent.TAP_CONFIRM)}
                            >
                                {saving ? COPY.saving : (mode === 'admin' ? COPY.createTheme : COPY.suggestTheme)}
                            </PulseButton>
                        </div>
                    </form>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
